import React, { useMemo } from 'react';
import { Plus, Edit, Trash2, ChevronsUpDown, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Visual treatment for leads imported by the Lead Gen Agent: green tint
// + saturated left border. We DON'T badge the company name — the source
// column ("Lead Gen Agent") already says who imported it, so the badge
// was redundant. The row colour alone is enough at a glance.
const isAgentLead = (l) => {
  const src = (l?.lead_source || '').toLowerCase().trim();
  return src === 'lead gen agent' || src === 'apollo lead gen';
};
const AGENT_ROW_CLS = 'bg-emerald-50 hover:bg-emerald-100 border-l-4 border-[#679632]';

const LeadsTable = ({ 
  leads, 
  loading, 
  onAddLead, 
  onEditLead, 
  onDeleteLead,
  onCloseDeal,
  sortOrder = 'desc',
  onToggleSort
}) => {
  const { isAdmin, user } = useAuth();
  
  // Check if user can delete (admin or Hima Sree/Shree)
  const canDelete = () => {
    if (isAdmin()) return true;
    if (!user?.name) return false;
    const userName = user.name.toLowerCase().trim();
    // Check for "Hima Sree", "Shree", or variations
    return userName.includes('hima') || userName.includes('shree') || userName === 'shree';
  };
  // Group leads by company_group_id, but merge duplicates in same location
  const groupedLeads = useMemo(() => {
    const groups = new Map();
    const ungrouped = [];
    
    // Case-, space-, and punctuation-insensitive canonical key.
    // Mirrors backend `normalizeCompanyKey` so "FlexPower", "Flex Power", and
    // "Flex-Power" collapse to the same key; keeps UI merging in sync with
    // server-side grouping.
    const normalizeName = (name) => {
      if (!name) return '';
      let s = String(name).toLowerCase().trim();
      const suffixRe = /\s*[-,]?\s*(solar|wind|belgium|portugal|spain|italy|germany|france|netherlands|uk|usa|us|gmbh|inc|llc|ltd|corp|co\.?|a\/s|ag|group|corporation|company|limited)\s*\.?$/i;
      let prev;
      do { prev = s; s = s.replace(suffixRe, '').trim(); } while (prev !== s);
      return s.replace(/[\s\-._,]+/g, '');
    };
    
    // Normalize location for comparison
    const normalizeLocation = (city, country) => {
      const cityNorm = (city || '').toLowerCase().trim();
      const countryNorm = (country || '').toLowerCase().trim();
      return `${cityNorm}::${countryNorm}`;
    };
    
    // First, group by company_group_id
    const tempGroups = new Map();
    leads.forEach(lead => {
      const groupId = lead.company_group_id;
      
      if (groupId) {
        if (!tempGroups.has(groupId)) {
          tempGroups.set(groupId, []);
        }
        tempGroups.get(groupId).push(lead);
      } else {
        ungrouped.push(lead);
      }
    });
    
    // Now, for each group, merge duplicates in the same location
    tempGroups.forEach((group, groupId) => {
      // Prefer leads with location; if none have location, use full group so new leads still show
      const validLeads = group.filter(lead => {
        const hasCity = lead.city && lead.city.trim() && lead.city !== '-';
        const hasCountry = lead.country && lead.country.trim() && lead.country !== '-';
        return hasCity || hasCountry;
      });
      const leadsToProcess = validLeads.length > 0 ? validLeads : group;
      
      // Group by normalized name + country (to handle same country with/without city)
      const countryGroups = new Map();
      leadsToProcess.forEach(lead => {
        const normalizedName = normalizeName(lead.company_name);
        const country = (lead.country || '').toLowerCase().trim() || 'unknown';
        const key = `${normalizedName}::${country}`;
        
        if (!countryGroups.has(key)) {
          countryGroups.set(key, []);
        }
        countryGroups.get(key).push(lead);
      });
      
      // For each country group, merge leads with same country
      const mergedByCountry = [];
      countryGroups.forEach((countryGroup, key) => {
        if (countryGroup.length === 1) {
          mergedByCountry.push(countryGroup[0]);
        } else {
          // Multiple leads with same country - prefer ones with city data
          // First, separate leads with city vs without city
          const withCity = countryGroup.filter(l => l.city && l.city.trim() && l.city !== '-');
          const withoutCity = countryGroup.filter(l => !l.city || !l.city.trim() || l.city === '-');
          
          // If there are leads with city, use only those (and merge among them)
          // Otherwise, use leads without city
          const leadsToMerge = withCity.length > 0 ? withCity : withoutCity;
          
          // Merge leads with same normalized name and same country
          // Group by full location (city + country)
          const locationGroups = new Map();
          leadsToMerge.forEach(lead => {
            const location = normalizeLocation(lead.city, lead.country);
            const locationKey = `${normalizeName(lead.company_name)}::${location}`;
            
            if (!locationGroups.has(locationKey)) {
              locationGroups.set(locationKey, []);
            }
            locationGroups.get(locationKey).push(lead);
          });
          
          // For each location group, keep only the best lead
          locationGroups.forEach((locationGroup) => {
            if (locationGroup.length > 1) {
              // Multiple leads in same location - merge them
              const bestLead = locationGroup.reduce((best, current) => {
                const stagePriority = { 'Proposal': 3, 'Contacted': 2, 'New': 1 };
                const currentStage = stagePriority[current.stage] || 0;
                const bestStage = stagePriority[best.stage] || 0;
                
                if (currentStage > bestStage) return current;
                if (currentStage === bestStage) {
                  const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
                  const currentPriority = priorityOrder[current.priority] || 0;
                  const bestPriority = priorityOrder[best.priority] || 0;
                  if (currentPriority > bestPriority) return current;
                  if (currentPriority === bestPriority) {
                    const currentHasSolar = /\s*-\s*solar\s*$/i.test(current.company_name);
                    const bestHasSolar = /\s*-\s*solar\s*$/i.test(best.company_name);
                    if (bestHasSolar && !currentHasSolar) return current;
                  }
                }
                return best;
              }, locationGroup[0]);
              mergedByCountry.push(bestLead);
            } else {
              mergedByCountry.push(locationGroup[0]);
            }
          });
        }
      });
      
      // Only render as a "(N divisions)" group when there are 2+ distinct
      // locations. Same-name same-division leads still share a backend
      // company_group_id (so reports stay consistent), but in the table they
      // collapse into one regular row — a "(1 divisions)" badge would be
      // visual noise.
      const uniqueLocations = new Set();
      mergedByCountry.forEach(lead => {
        const loc = normalizeLocation(lead.city, lead.country);
        uniqueLocations.add(loc);
      });

      if (uniqueLocations.size > 1) {
        groups.set(groupId, mergedByCountry);
      } else if (mergedByCountry.length > 0) {
        ungrouped.push(mergedByCountry[0]);
      }
    });
    
    // Also merge ungrouped leads that have duplicates with "- Solar" suffix in same location
    // Include all ungrouped leads (even with no city/country) so newly added leads always show
    const validUngrouped = ungrouped;
    
    // Group ungrouped leads by normalized name + country (to handle same country with/without city)
    const ungroupedCountryGroups = new Map();
    validUngrouped.forEach(lead => {
      const normalizedName = normalizeName(lead.company_name);
      const country = (lead.country || '').toLowerCase().trim() || 'unknown';
      const key = `${normalizedName}::${country}`;
      
      if (!ungroupedCountryGroups.has(key)) {
        ungroupedCountryGroups.set(key, []);
      }
      ungroupedCountryGroups.get(key).push(lead);
    });
    
    // Merge duplicates in ungrouped leads
    const mergedUngrouped = [];
    ungroupedCountryGroups.forEach((countryGroup, key) => {
      if (countryGroup.length === 1) {
        mergedUngrouped.push(countryGroup[0]);
      } else {
        // Multiple leads with same country - prefer ones with city data
        const withCity = countryGroup.filter(l => l.city && l.city.trim() && l.city !== '-');
        const withoutCity = countryGroup.filter(l => !l.city || !l.city.trim() || l.city === '-');
        
        // If there are leads with city, use only those (and merge among them)
        // Otherwise, use leads without city
        const leadsToMerge = withCity.length > 0 ? withCity : withoutCity;
        
        // Group by full location (city + country)
        const locationGroups = new Map();
        leadsToMerge.forEach(lead => {
          const location = normalizeLocation(lead.city, lead.country);
          const locationKey = `${normalizeName(lead.company_name)}::${location}`;
          
          if (!locationGroups.has(locationKey)) {
            locationGroups.set(locationKey, []);
          }
          locationGroups.get(locationKey).push(lead);
        });
        
        // For each location group, keep only the best lead
        locationGroups.forEach((locationGroup) => {
          if (locationGroup.length > 1) {
            // Multiple leads in same location - merge them
            const bestLead = locationGroup.reduce((best, current) => {
              const stagePriority = { 'Proposal': 3, 'Contacted': 2, 'New': 1 };
              const currentStage = stagePriority[current.stage] || 0;
              const bestStage = stagePriority[best.stage] || 0;
              
              if (currentStage > bestStage) return current;
              if (currentStage === bestStage) {
                const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
                const currentPriority = priorityOrder[current.priority] || 0;
                const bestPriority = priorityOrder[best.priority] || 0;
                if (currentPriority > bestPriority) return current;
                // Prefer the one without "- Solar" suffix
                if (currentPriority === bestPriority) {
                  const currentHasSolar = /\s*-\s*solar\s*$/i.test(current.company_name);
                  const bestHasSolar = /\s*-\s*solar\s*$/i.test(best.company_name);
                  if (bestHasSolar && !currentHasSolar) return current;
                }
              }
              return best;
            }, locationGroup[0]);
            mergedUngrouped.push(bestLead);
          } else {
            mergedUngrouped.push(locationGroup[0]);
          }
        });
      }
    });
    
    // Convert groups to array format
    const groupedArray = Array.from(groups.values()).map(group => {
      // Find the most recent lead (by date)
      const mostRecentLead = group.reduce((latest, lead) => {
        const leadDate = new Date((lead.lead_date || '1900-01-01') + 'T00:00:00');
        const latestDate = new Date((latest.lead_date || '1900-01-01') + 'T00:00:00');
        return leadDate > latestDate ? lead : latest;
      }, group[0]);
      
      // Find the lead with the highest priority/stage
      const bestStageLead = group.find(l => l.stage === 'Proposal') || 
                           group.find(l => l.stage === 'Contacted') || 
                           group[0];
      
      // Find the lead with POC details (prefer one with actual POC data)
      const leadWithPOC = group.find(l => l.poc_name && l.poc_name !== '-') ||
                         group.find(l => l.email && l.email !== '-') ||
                         group[0];

      // Find the best lead for location display: must have BOTH city and country from the SAME lead
      const hasCity = (l) => l.city && l.city.trim() && l.city !== '-';
      const hasCountry = (l) => l.country && l.country.trim() && l.country !== '-';
      const locationLead =
        // First: most recent lead if it has both city and country
        (hasCity(mostRecentLead) && hasCountry(mostRecentLead) ? mostRecentLead : null) ||
        // Second: any lead with both city and country
        group.find(l => hasCity(l) && hasCountry(l)) ||
        // Third: any lead with at least country
        group.find(l => hasCountry(l)) ||
        // Fallback: most recent lead
        mostRecentLead;

      return {
        isGroup: true,
        groupId: group[0].company_group_id,
        leads: group,
        // Use the most recent date
        lead_date: mostRecentLead.lead_date,
        // Use clean company name (remove location suffixes)
        company_name: group[0].company_name.replace(/\s+(UK|USA|US|GmbH|Inc|LLC|Ltd|Corp)\s*$/i, '').trim(),
        // Use best stage and priority
        stage: bestStageLead.stage,
        priority: bestStageLead.priority,
        // Use BDE from the most recent lead
        bde_name: mostRecentLead.bde_name,
        // Use POC details from lead with POC (or most recent)
        poc_name: leadWithPOC.poc_name || (group.find(l => l.poc_name && l.poc_name !== '-')?.poc_name) || '-',
        email: leadWithPOC.email || (group.find(l => l.email && l.email !== '-')?.email) || '-',
        // Show primary location from the SAME lead (never mix city from one lead and country from another)
        city: locationLead.city || '-',
        country: locationLead.country || '-',
        locations: group.map(l => ({
          id: l.id,
          name: l.company_name,
          city: l.city,
          country: l.country,
          poc_name: l.poc_name,
          email: l.email,
          stage: l.stage,
          priority: l.priority,
          lead_date: l.lead_date,
          bde_name: l.bde_name
        }))
      };
    });
    
    // Combine grouped and merged ungrouped
    return [...groupedArray, ...mergedUngrouped.map(lead => ({ isGroup: false, lead }))];
  }, [leads]);
  
  // Sort grouped leads. sortOrder: 'desc' = Recent first, 'asc' = Oldest first, 'az' = A→Z by company.
  const sortedLeads = [...groupedLeads].sort((a, b) => {
    // Onboarded always sinks to the bottom regardless of sort mode.
    const stageA = a.stage || a.lead?.stage;
    const stageB = b.stage || b.lead?.stage;
    if (stageA === 'Onboarded' && stageB !== 'Onboarded') return 1;
    if (stageA !== 'Onboarded' && stageB === 'Onboarded') return -1;

    if (sortOrder === 'az') {
      const nameA = a.company_name || a.lead?.company_name || '';
      const nameB = b.company_name || b.lead?.company_name || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    }

    const dateA = new Date((a.lead_date || a.lead?.lead_date || '1900-01-01') + 'T00:00:00');
    const dateB = new Date((b.lead_date || b.lead?.lead_date || '1900-01-01') + 'T00:00:00');
    return sortOrder === 'desc' ? (dateB - dateA) : (dateA - dateB);
  });
  
  const [expandedGroups, setExpandedGroups] = React.useState(new Set());
  
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="bg-white rounded shadow border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">All Leads ({groupedLeads.length})</h3>
            <label className="flex items-center gap-1 text-gray-600 text-sm border border-gray-300 rounded px-2 py-1">
              <ChevronsUpDown size={16} />
              <select
                value={sortOrder}
                onChange={(e) => onToggleSort && onToggleSort(e.target.value)}
                className="bg-transparent text-sm text-gray-700 focus:outline-none cursor-pointer"
                title="Sort leads"
              >
                <option value="desc">Recent</option>
                <option value="asc">Oldest</option>
                <option value="az">A → Z</option>
              </select>
            </label>
          </div>
          <button 
            onClick={onAddLead}
            className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded hover:bg-[#587f2b] text-sm"
          >
            <Plus size={18} />
            Add Lead
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#679632]"></div>
          <p className="mt-4 text-gray-600">Loading leads...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">POC</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">BDE</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedLeads.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    No leads found. Add your first lead to get started!
                  </td>
                </tr>
              ) : (
                sortedLeads.map((item, index) => {
                  if (item.isGroup) {
                    const isExpanded = expandedGroups.has(item.groupId);
                    const locationsCount = item.locations.length;
                    
                    return (
                      <React.Fragment key={item.groupId}>
                        <tr
                          className={`cursor-pointer ${item.leads?.some(isAgentLead) ? AGENT_ROW_CLS : 'hover:bg-gray-50 border-l-2 border-[#679632]'}`}
                          onClick={() => toggleGroup(item.groupId)}
                        >
                          <td className="px-4 py-2 text-sm">
                            {item.lead_date ? new Date(item.lead_date + 'T00:00:00').toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <span>{item.company_name}</span>
                              <span className="text-xs text-gray-500">({locationsCount} divisions)</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(item.groupId);
                                }}
                                className="flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                                title={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? <ChevronUp size={18} className="text-gray-700" /> : <ChevronDown size={18} className="text-gray-700" />}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">{item.poc_name || '-'}</td>
                          <td className="px-4 py-2 text-sm">{item.email || '-'}</td>
                          <td className="px-4 py-2 text-sm">{item.city || '-'}</td>
                          <td className="px-4 py-2 text-sm">{item.country || '-'}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              item.stage === 'Onboarded' ? 'bg-red-100 text-red-700' :
                              item.stage === 'Important - But Contact Later' ? 'bg-orange-100 text-orange-700' :
                              'bg-[#e1f0da] text-[#587f2b]'
                            }`}>
                              {item.stage || 'New'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              item.priority === 'High' ? 'bg-red-100 text-red-700' :
                              item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {item.priority || 'Medium'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{item.bde_name || '-'}</td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              <button 
                                onClick={() => onEditLead(item.leads[0])}
                                className="text-[#679632] hover:text-[#496821]"
                                title="Edit lead"
                              >
                                <Edit size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && item.locations.map((location, locIdx) => (
                          <tr
                            key={`${item.groupId}-${location.id}`}
                            className={`cursor-pointer ${isAgentLead(item.leads.find(l => l.id === location.id)) ? AGENT_ROW_CLS : 'hover:bg-gray-50'}`}
                            onClick={() => onEditLead(item.leads.find(l => l.id === location.id))}
                          >
                            <td className="px-4 py-2 text-sm">
                              {location.lead_date ? new Date(location.lead_date + 'T00:00:00').toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              }) : ''}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className="text-gray-600 pl-6">{location.name}</span>
                            </td>
                            <td className="px-4 py-2 text-sm">{location.poc_name || '-'}</td>
                            <td className="px-4 py-2 text-sm">{location.email || '-'}</td>
                            <td className="px-4 py-2 text-sm">{location.city || '-'}</td>
                            <td className="px-4 py-2 text-sm">{location.country || '-'}</td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                location.stage === 'Onboarded' ? 'bg-red-100 text-red-700' :
                                location.stage === 'Important - But Contact Later' ? 'bg-orange-100 text-orange-700' :
                                'bg-[#e1f0da] text-[#587f2b]'
                              }`}>
                                {location.stage || 'New'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                location.priority === 'High' ? 'bg-red-100 text-red-700' :
                                location.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {location.priority || 'Medium'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">{location.bde_name || '-'}</td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  onClick={() => onEditLead(item.leads.find(l => l.id === location.id))}
                                  className="text-[#679632] hover:text-[#496821]"
                                  title="Edit lead"
                                >
                                  <Edit size={16} />
                                </button>
                                {canDelete() && (
                                  <button 
                                    onClick={() => onDeleteLead(location.id)}
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete lead"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  } else {
                    const lead = item.lead;
                    return (
                      <tr
                        key={lead.id}
                        className={`cursor-pointer ${isAgentLead(lead) ? AGENT_ROW_CLS : 'hover:bg-gray-50'}`}
                        onClick={() => onEditLead(lead)}
                      >
                    <td className="px-4 py-2 text-sm">
                      {lead.lead_date ? new Date(lead.lead_date + 'T00:00:00').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      }) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium">{lead.company_name}</td>
                    <td className="px-4 py-2 text-sm">{lead.poc_name || '-'}</td>
                    <td className="px-4 py-2 text-sm">{lead.email || '-'}</td>
                    <td className="px-4 py-2 text-sm">{lead.city || '-'}</td>
                    <td className="px-4 py-2 text-sm">{lead.country || '-'}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                            lead.stage === 'Onboarded' ? 'bg-red-100 text-red-700' :
                        lead.stage === 'Important - But Contact Later' ? 'bg-orange-100 text-orange-700' :
                        'bg-[#e1f0da] text-[#587f2b]'
                      }`}>
                        {lead.stage || 'New'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        lead.priority === 'High' ? 'bg-red-100 text-red-700' :
                        lead.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {lead.priority || 'Medium'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">{lead.bde_name || '-'}</td>
                    <td className="px-4 py-2 text-sm">
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => onEditLead(lead)}
                          className="text-[#679632] hover:text-[#496821]"
                          title="Edit lead"
                        >
                          <Edit size={16} />
                        </button>
                        {canDelete() && (
                          <button 
                            onClick={() => onDeleteLead(lead.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete lead"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                    );
                  }
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeadsTable;
